import { Checkbox } from 'components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import Loader from 'components/Loader.tsx';
import PartyName from 'components/party/PartyName.tsx';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import HoursDisplay from 'components/util/HoursDisplay.tsx';
import MoneyDisplay, { formatAmount } from 'components/util/MoneyDisplay.tsx';
import { getBudgetInfo } from 'pages/invoices/util/invoiceUtil.tsx';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useGetAddableMattersQuery } from 'store/api/invoice-details.ts';
import { BillableMatterListItem } from 'types/billable-matters.ts';
import { ClientParty } from 'types/matters.ts';
import { t } from 'utils/getTranslations';
import ConfirmationModal from '../confirmation/ConfirmationModal.tsx';
import { ModalFooter } from '../ModalFooter.tsx';
import { ModalHeader } from '../ModalHeader.tsx';
import { useModal } from '../useModal.ts';

interface Props {
    invoiceId: number;
    /** Names the invoice in the title, where the new design's copy asks for it. */
    invoiceNumber?: string;
    onClose: () => void;
    onSubmit: (matterIds: number[]) => void;
}

interface ClientGroup {
    client: ClientParty;
    matters: BillableMatterListItem[];
}

/** The case's number and its name apart, as the new design sets them: `fullName` is the number, a space and the name. */
const caseNumberOf = (matter: BillableMatterListItem) =>
    matter.fullName.endsWith(matter.name) ? matter.fullName.slice(0, -matter.name.length).trim() : '';

const groupCases = (matters: BillableMatterListItem[]): ClientGroup[] => {
    const grouped = new Map<number, ClientGroup>();
    matters.forEach((matter) => {
        if (!matter.clientParty) {
            return;
        }
        const clientId = matter.clientParty.id;
        const existing = grouped.get(clientId);
        if (existing) {
            existing.matters.push(matter);
        } else {
            grouped.set(clientId, { client: matter.clientParty, matters: [matter] });
        }
    });
    return Array.from(grouped.values());
};

const AddCaseToJointInvoiceModal = ({ invoiceId, invoiceNumber, onClose, onSubmit }: Props) => {
    const isNew = useIsNewDesign();
    const { openModal, closeModal } = useModal();
    const [selectedMatterIds, setSelectedMatterIds] = useState<Set<number>>(new Set());

    const { data: addableMatters, isLoading } = useGetAddableMattersQuery(invoiceId);

    const clientGroups = useMemo(() => groupCases(addableMatters || []), [addableMatters]);
    // The cases listed share the invoice's currency, so the new design names it once, in the headers.
    const currency = addableMatters?.[0]?.currencyDetails?.originalCurrency ?? '';
    const figureClass = isNew ? 'text-end' : undefined;
    const money = (matter: BillableMatterListItem, amount: number) =>
        isNew ? (
            formatAmount(amount)
        ) : (
            <MoneyDisplay amount={amount} currency={matter.currencyDetails?.originalCurrency ?? ''} />
        );

    const handleMatterToggle = useCallback((matterId: number, checked: boolean) => {
        setSelectedMatterIds((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.add(matterId);
            } else {
                next.delete(matterId);
            }
            return next;
        });
    }, []);

    const handleClientSelectAll = useCallback((matterIds: number[], selectAll: boolean) => {
        setSelectedMatterIds((prev) => {
            const next = new Set(prev);
            matterIds.forEach((id) => (selectAll ? next.add(id) : next.delete(id)));
            return next;
        });
    }, []);

    const handleOnSave = useCallback(() => {
        if (selectedMatterIds.size === 0) {
            return;
        }
        openModal(
            <ConfirmationModal
                onCancel={closeModal}
                onConfirm={() => {
                    onSubmit(Array.from(selectedMatterIds));
                    closeModal();
                    onClose();
                }}
                content={<p className="text-body1">{t('invoices.add-case-modal.add-confirmation-text')}</p>}
                cancelId="no"
                alert={{
                    titleId: 'invoices.confirm.add-cases-title',
                    titleValues: { count: selectedMatterIds.size },
                    description: t('invoices.add-case-modal.add-confirmation-text'),
                    confirmId: 'invoices.confirm.add-cases',
                    confirmValues: { count: selectedMatterIds.size },
                }}
            />
        );
    }, [selectedMatterIds, onClose, onSubmit, openModal, closeModal]);

    const renderEmpty = () => (
        <p className="p-4 text-xs leading-5">
            <FormattedMessage id="invoices.add-case-modal.no-addable-cases" />
        </p>
    );

    const renderGroup = (group: ClientGroup) => {
        const groupMatterIds = group.matters.map((m) => m.id);
        const selectedInGroup = groupMatterIds.filter((id) => selectedMatterIds.has(id)).length;
        const allSelected = selectedInGroup === groupMatterIds.length;
        const someSelected = selectedInGroup > 0 && !allSelected;

        return (
            <div key={`client-${group.client.id}`} className="border-divider overflow-hidden rounded-lg border">
                <div className="bg-grey-50 border-divider border-b px-4 py-2.5 font-semibold">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            variant="secondary"
                            checked={allSelected}
                            indeterminate={someSelected}
                            onCheckedChange={(checked) => handleClientSelectAll(groupMatterIds, checked)}
                        />
                        <PartyName party={group.client} />
                    </div>
                </div>
                <div>
                    <div>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-10" />
                                    <TableHead>{t('matters.case')}</TableHead>
                                    <TableHead>{t('invoices.cap-fixed-fee')}</TableHead>
                                    <TableHead className={figureClass}>{t('stats.uninvoiced-hours')}</TableHead>
                                    <TableHead className={figureClass}>
                                        {isNew
                                            ? t('invoices.add-case-modal.uninvoiced-expenses', { currency })
                                            : t('stats.uninvoiced-expenses')}
                                    </TableHead>
                                    <TableHead className={figureClass}>
                                        {isNew
                                            ? t('invoices.add-case-modal.uninvoiced-amount', { currency })
                                            : t('stats.uninvoiced-amount')}
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {group.matters.map((matter) => {
                                    const isChecked = selectedMatterIds.has(matter.id);
                                    return (
                                        <TableRow key={matter.id}>
                                            <TableCell className="w-10">
                                                <Checkbox
                                                    variant="secondary"
                                                    checked={isChecked}
                                                    onCheckedChange={(checked) =>
                                                        handleMatterToggle(matter.id, checked)
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {isNew ? (
                                                    <p className="text-body1">
                                                        <span className="text-foreground-tertiary text-meta font-mono">
                                                            {caseNumberOf(matter)}
                                                        </span>{' '}
                                                        <span className="font-semibold">{matter.name}</span>
                                                    </p>
                                                ) : (
                                                    <p className="text-body1 font-semibold">{matter.fullName}</p>
                                                )}
                                            </TableCell>
                                            <TableCell>{getBudgetInfo(matter) || '-'}</TableCell>
                                            <TableCell className={figureClass}>
                                                <HoursDisplay value={matter.matterStats?.uninvoicedTimeInHours} />
                                            </TableCell>
                                            <TableCell className={figureClass}>
                                                {money(
                                                    matter,
                                                    matter.currencyDetails?.uninvoicedExpensesInOriginalCurrency ?? 0
                                                )}
                                            </TableCell>
                                            <TableCell className={figureClass}>
                                                {money(
                                                    matter,
                                                    (matter.currencyDetails?.uninvoicedAmountInOriginalCurrency ?? 0) +
                                                        (matter.currencyDetails?.uninvoicedExpensesInOriginalCurrency ??
                                                            0)
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <ModalHeader
                kit
                title={t('invoices.add-case-modal.title', { number: invoiceNumber || 'none' })}
                description={t('invoices.add-case-modal.description')}
                onCancel={onClose}
            />
            {/* The new design's dialog hugs its table, scrolling past 70% of the window. */}
            <div className="new:h-auto new:max-h-[70vh] h-[800px] overflow-y-auto p-6 max-sm:px-4">
                {isLoading ? (
                    <Loader />
                ) : clientGroups.length === 0 ? (
                    renderEmpty()
                ) : (
                    <div className="flex flex-col gap-4">{clientGroups.map(renderGroup)}</div>
                )}
            </div>
            <ModalFooter
                kit
                saveButtonTextId={isNew ? 'invoices.confirm.add-cases' : 'invoices.add-case'}
                saveButtonValues={isNew ? { count: selectedMatterIds.size } : undefined}
                disableSave={selectedMatterIds.size === 0}
                onCancel={onClose}
                onSave={handleOnSave}
            />
        </>
    );
};

export default AddCaseToJointInvoiceModal;
